import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useCompanyStore, Company } from './stores/company';
import { useLicenseStore } from './stores/license';

// Multi-tenant company management: add/edit/remove/select, encrypted vault persistence
export function CompanyManager() {
  const { t } = useTranslation();
  const { valid } = useLicenseStore();
  const { companies, activeId, setActive, addCompany, updateCompany, removeCompany, load } = useCompanyStore();

  const [draft, setDraft] = useState<Omit<Company, 'id'>>({ name: '', country: '', industry: '', employees: 0 });
  const [editId, setEditId] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const activeCompany = useMemo(() => companies.find((c) => c.id === activeId) ?? null, [companies, activeId]);

  const onSubmit = async () => {
    if (!valid) return;
    if (editId) {
      await updateCompany(editId, draft);
      setEditId(null);
    } else {
      await addCompany(draft);
    }
    setDraft({ name: '', country: '', industry: '', employees: 0 });
  };

  const startEdit = (c: Company) => {
    setEditId(c.id);
    setDraft({ name: c.name, country: c.country, industry: c.industry, employees: c.employees });
  };

  const disabled = !valid;

  return (
    <div className="space-y-4">
      {!valid && (
        <div className="border border-evat-gold/40 rounded p-3 text-evat-gold">
          {t('license.invalidNotice') || 'Enter a valid license key to enable actions.'}
        </div>
      )}

      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-evat-neon/50 rounded p-4">
          <div className="font-orbitron text-evat-neon mb-2">{editId ? (t('company.edit') || 'Edit Company') : (t('company.add') || 'Add Company')}</div>
          <div className="space-y-2">
            <input
              disabled={disabled}
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder={t('company.name') || 'Name'}
              className="w-full bg-black text-neutral-100 border border-evat-neon rounded px-3 py-2"
            />
            <input
              disabled={disabled}
              value={draft.country}
              onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}
              placeholder={t('company.country') || 'Country'}
              className="w-full bg-black text-neutral-100 border border-evat-neon rounded px-3 py-2"
            />
            <input
              disabled={disabled}
              value={draft.industry}
              onChange={(e) => setDraft((d) => ({ ...d, industry: e.target.value }))}
              placeholder={t('company.industry') || 'Industry'}
              className="w-full bg-black text-neutral-100 border border-evat-neon rounded px-3 py-2"
            />
            <input
              disabled={disabled}
              type="number"
              value={draft.employees}
              onChange={(e) => setDraft((d) => ({ ...d, employees: Number(e.target.value) }))}
              placeholder={t('company.employees') || 'Employees'}
              className="w-full bg-black text-neutral-100 border border-evat-neon rounded px-3 py-2"
            />
            <div className="flex gap-2">
              <button disabled={disabled} onClick={onSubmit} className="px-3 py-2 border border-evat-neon text-evat-neon rounded hover:shadow-neon">
                {editId ? (t('company.update') || 'Update') : (t('company.add') || 'Add')}
              </button>
              {editId && (
                <button disabled={disabled} onClick={() => setEditId(null)} className="px-3 py-2 border border-neutral-600 text-neutral-400 rounded">
                  {t('company.cancel') || 'Cancel'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border border-evat-gold/40 rounded p-4">
          <div className="font-orbitron text-evat-gold mb-2">{t('company.list') || 'Companies'}</div>
          <div className="space-y-2">
            {companies.map((c) => (
              <div key={c.id} className={`flex items-center justify-between border rounded px-3 py-2 ${activeId === c.id ? 'border-evat-neon' : 'border-neutral-700'}`}>
                <div>
                  <div className="text-neutral-100">{c.name}</div>
                  <div className="text-xs text-neutral-400">{c.country} • {c.industry} • {c.employees}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button disabled={disabled} onClick={() => setActive(c.id)} className="px-2 py-1 border border-evat-gold text-evat-gold rounded">{t('company.select') || 'Select'}</button>
                  <button disabled={disabled} onClick={() => startEdit(c)} className="px-2 py-1 border border-evat-neon text-evat-neon rounded">{t('company.edit') || 'Edit'}</button>
                  <button disabled={disabled} onClick={() => removeCompany(c.id)} className="px-2 py-1 border border-red-500 text-red-400 rounded">{t('company.remove') || 'Remove'}</button>
                </div>
              </div>
            ))}
            {companies.length === 0 && (
              <div className="text-neutral-400 text-sm">{t('company.empty') || 'No companies yet.'}</div>
            )}
          </div>
        </div>
      </motion.div>

      {activeCompany && (
        <div className="text-sm text-neutral-300">
          {(t('company.active') || 'Active company') + ': '}<span className="text-evat-gold">{activeCompany.name}</span>
        </div>
      )}
    </div>
  );
}

export default CompanyManager;



















